import { Fragment, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface BreadcrumbOption {
  label: string;
  to: string;
}

interface BreadcrumbLinkItem {
  label: string;
  to?: string;
  options?: never;
  selectorLabel?: never;
}

interface BreadcrumbSelectorItem {
  label: string;
  options: BreadcrumbOption[];
  selectorLabel: string;
  to?: never;
}

export type BreadcrumbItem = BreadcrumbLinkItem | BreadcrumbSelectorItem;

export function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m3 10 9-7 9 7v10a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V10Z" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m7 9.5 5 5 5-5" />
    </svg>
  );
}

function BreadcrumbSelector({ item }: { item: BreadcrumbSelectorItem }) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    const closeOnPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnPointerDown);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnPointerDown);
    };
  }, [open]);

  return (
    <div className="domain-breadcrumb__selector" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        className="domain-breadcrumb__selector-button"
        aria-label={item.selectorLabel}
        aria-controls={listId}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <strong aria-current="page">{item.label}</strong>
        <ChevronIcon />
      </button>
      <div
        id={listId}
        className="domain-breadcrumb__selector-options"
        role="group"
        aria-label="Módulos disponibles"
        hidden={!open}
      >
        {item.options.map((option) => (
          <Link key={option.to} to={option.to} onClick={() => setOpen(false)}>
            {option.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function Breadcrumb({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav className="domain-breadcrumb" aria-label="Ruta de navegación">
      <Link
        className="domain-breadcrumb__home"
        to="/"
        aria-label="Ir al inicio"
        title="Inicio"
      >
        <HomeIcon />
      </Link>
      {items.map((item, index) => {
        const current = index === items.length - 1;
        return (
          <Fragment key={`${item.to ?? "current"}-${item.label}`}>
            <span aria-hidden="true">/</span>
            {item.options ? (
              <BreadcrumbSelector item={item} />
            ) : item.to ? (
              <Link className="domain-breadcrumb__link" to={item.to}>
                {item.label}
              </Link>
            ) : current ? (
              <strong aria-current="page">{item.label}</strong>
            ) : (
              <span>{item.label}</span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
